import type {
  SidebarProjectSortOrder,
  SidebarThreadSortOrder,
} from "@cmmd-forge/contracts/settings";
import { ListFilterIcon } from "lucide-react";
import type { SidebarDensity } from "~/uiStateStore";
import { DENSE_RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import {
  Menu,
  MenuCheckboxItem,
  MenuPopup,
  MenuRadioGroup,
  MenuRadioItem,
  MenuSeparator,
  MenuSub,
  MenuSubPopup,
  MenuSubTrigger,
  MenuTrigger,
} from "../ui/menu";
import {
  SIDEBAR_ENVIRONMENT_FILTER_LABEL,
  SIDEBAR_GROUP_BY_LABEL,
  SIDEBAR_STATUS_FILTER_LABEL,
  type SidebarEnvironmentFilter,
  type SidebarGroupBy,
  type SidebarStatusFilter,
  type SidebarViewFilters,
} from "./sidebarViewFilters";

export const PROJECTS_WORKSPACE_DENSITY_ID = "__projects__";
export const LEGACY_LOCAL_WORKSPACE_DENSITY_ID = "__local__";

const PROJECT_SORT_ORDER_LABEL: Record<SidebarProjectSortOrder, string> = {
  updated_at: "Recent activity",
  created_at: "Date added",
  manual: "Manual",
};

const THREAD_SORT_ORDER_LABEL: Record<SidebarThreadSortOrder, string> = {
  updated_at: "Recent activity",
  created_at: "Date created",
};

const DENSITY_LABEL: Record<SidebarDensity, string> = {
  comfortable: "Comfortable",
  compact: "Compact",
};

interface ViewOptionSubmenuProps<Value extends string> {
  readonly label: string;
  readonly value: Value;
  readonly options: ReadonlyArray<readonly [Value, string]>;
  readonly onChange: (value: Value) => void;
}

/**
 * One "Label  Current value ›" row that opens a radio submenu.
 *
 * The radio items live INSIDE `MenuRadioGroup` — Base UI menu group parts throw
 * when rendered with no group in context, which previously took the whole route
 * down (production error #31).
 */
function ViewOptionSubmenu<Value extends string>({
  label,
  value,
  options,
  onChange,
}: ViewOptionSubmenuProps<Value>) {
  return (
    <MenuSub>
      <MenuSubTrigger>
        <span className="whitespace-nowrap">{label}</span>
        <span className="ms-auto ps-6 whitespace-nowrap text-muted-foreground text-xs">
          {options.find(([candidate]) => candidate === value)?.[1] ?? ""}
        </span>
      </MenuSubTrigger>
      <MenuSubPopup className="w-44">
        <MenuRadioGroup
          value={value}
          onValueChange={(next) => {
            if (!next || next === value) return;
            onChange(next as Value);
          }}
        >
          {options.map(([candidate, optionLabel]) => (
            <MenuRadioItem key={candidate} value={candidate}>
              {optionLabel}
            </MenuRadioItem>
          ))}
        </MenuRadioGroup>
      </MenuSubPopup>
    </MenuSub>
  );
}

interface ProjectSidebarViewOptionsMenuProps {
  readonly density: SidebarDensity;
  readonly projectSortOrder: SidebarProjectSortOrder;
  readonly threadSortOrder: SidebarThreadSortOrder;
  readonly filters: SidebarViewFilters;
  readonly onDensityChange: (density: SidebarDensity) => void;
  readonly onProjectSortOrderChange: (order: SidebarProjectSortOrder) => void;
  readonly onThreadSortOrderChange: (order: SidebarThreadSortOrder) => void;
  readonly onFiltersChange: (filters: SidebarViewFilters) => void;
}

export function ProjectSidebarViewOptionsMenu(props: ProjectSidebarViewOptionsMenuProps) {
  const { filters, onFiltersChange } = props;

  return (
    <Menu>
      <MenuTrigger
        aria-label="Project list options"
        className={`${DENSE_RESPONSIVE_ICON_BUTTON_CLASS} inline-flex cursor-pointer items-center justify-center text-muted-foreground/70 transition-colors hover:bg-accent/65 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring`}
      >
        <ListFilterIcon aria-hidden="true" className="size-3.5" />
      </MenuTrigger>
      <MenuPopup align="end" className="w-72">
        <ViewOptionSubmenu<SidebarStatusFilter>
          label="Status"
          value={filters.status}
          options={[
            ["active", SIDEBAR_STATUS_FILTER_LABEL.active],
            ["archived", SIDEBAR_STATUS_FILTER_LABEL.archived],
            ["all", SIDEBAR_STATUS_FILTER_LABEL.all],
          ]}
          onChange={(status) => onFiltersChange({ ...filters, status })}
        />
        <ViewOptionSubmenu<SidebarEnvironmentFilter>
          label="Environment"
          value={filters.environment}
          options={[
            ["all", SIDEBAR_ENVIRONMENT_FILTER_LABEL.all],
            ["cloud", SIDEBAR_ENVIRONMENT_FILTER_LABEL.cloud],
            ["desktop", SIDEBAR_ENVIRONMENT_FILTER_LABEL.desktop],
          ]}
          onChange={(environment) => onFiltersChange({ ...filters, environment })}
        />
        <MenuCheckboxItem
          checked={filters.showEmptyWorkspaces}
          variant="switch"
          onCheckedChange={(showEmptyWorkspaces) =>
            onFiltersChange({ ...filters, showEmptyWorkspaces })
          }
        >
          <span className="whitespace-nowrap">Show empty projects</span>
        </MenuCheckboxItem>
        <MenuSeparator />
        <ViewOptionSubmenu<SidebarGroupBy>
          label="Group by"
          value={filters.groupBy}
          options={[
            ["none", SIDEBAR_GROUP_BY_LABEL.none],
            ["environment", SIDEBAR_GROUP_BY_LABEL.environment],
          ]}
          onChange={(groupBy) => onFiltersChange({ ...filters, groupBy })}
        />
        <ViewOptionSubmenu<SidebarProjectSortOrder>
          label="Sort by"
          value={props.projectSortOrder}
          options={[
            ["updated_at", PROJECT_SORT_ORDER_LABEL.updated_at],
            ["created_at", PROJECT_SORT_ORDER_LABEL.created_at],
            ["manual", PROJECT_SORT_ORDER_LABEL.manual],
          ]}
          onChange={props.onProjectSortOrderChange}
        />
        <ViewOptionSubmenu<SidebarThreadSortOrder>
          label="Thread order"
          value={props.threadSortOrder}
          options={[
            ["updated_at", THREAD_SORT_ORDER_LABEL.updated_at],
            ["created_at", THREAD_SORT_ORDER_LABEL.created_at],
          ]}
          onChange={props.onThreadSortOrderChange}
        />
        <ViewOptionSubmenu<SidebarDensity>
          label="Density"
          value={props.density}
          options={[
            ["comfortable", DENSITY_LABEL.comfortable],
            ["compact", DENSITY_LABEL.compact],
          ]}
          onChange={props.onDensityChange}
        />
      </MenuPopup>
    </Menu>
  );
}
